/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useEffect } from "react";
import {
  TextField,
  DropZone,
  BlockStack,
  Text,
  Button,
  Thumbnail,
  Box,
  RadioButton,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { MultiComboOption } from "../MultiSelectCombobox";
import DateRangeList from "../DatePicker/DateRangeList";
import TranslateLink from "../TranslateLink";
import { PhoneNumberField } from "../PhoneNumberField/PhoneNumberField";
import NumberRangeField from "./NumberRangeField/NumberRangeField";
import { MultiSelect } from "./MultiSelect/MultiSelect";
import { useDebounce } from "@shared/hooks/common/useDebounce";
import TeammemberSelect, { Member } from "../TeammemberSelect";

/* same props interface */
interface ComboOption {
  label: string;
  value: string;
}

interface FilterComponentRendererProps {
  id:
    | "textInput"
    | "textArea"
    | "number"
    | "checkbox"
    | "phoneNumber"
    | "email"
    | "date"
    | "combobox"
    | "combeBoxMultiple"
    | "dropZone"
    | "teamMembers";
  label?: string;
  value?: string | number | boolean | string[];
  onChange: (newValue: any) => void;
  disabled?: boolean;
  helpText?: string;
  placeholder?: string;
  comboOptions?: ComboOption[];
  error?: string;
  isRequired?: boolean;
  file?: File;
  onDrop?: (_dropFiles: File[], acceptedFiles: File[]) => void;
  onRemoveFile?: (file: File) => void;
}

const FilterComponentRendererInner: React.FC<FilterComponentRendererProps> = ({
  isRequired,
  id,
  label,
  value,
  onChange,
  disabled = false,
  helpText,
  placeholder,
  comboOptions = [],
  error,
  file,
  onDrop,
  onRemoveFile,
}) => {
  const { t } = useTranslation();
  // Common label/placeholder
  const stringValue = value == null ? "" : String(value);
  const computedLabel = label ?? t("FilterComponentRenderer.label");

  const computedPlaceholder =
    placeholder ?? t("FilterComponentRenderer.placeholder");

  // Add local state for text input
  const [localValue, setLocalValue] = useState(stringValue);

  // Update local value when prop value changes
  useEffect(() => {
    setLocalValue(stringValue);
  }, [stringValue]);

  // Add debounced onChange
  const debouncedOnChange = useDebounce(onChange, 1000);

  // Multi-Select
  const [multiSelected, setMultiSelected] = useState<string[]>([]);
  useEffect(() => {
    if (id !== "combeBoxMultiple" && id !== "combobox") return;
    if (Array.isArray(value)) {
      setMultiSelected(value);
    } else if (typeof value === "string" && value.length > 0) {
      const splitted = value.split(",").map((v) => v.trim());
      setMultiSelected(splitted);
    } else {
      setMultiSelected([]);
    }
  }, [id, value]);

  const handleMultiSelectChange = useCallback(
    (newSelected: string[]) => {
      setMultiSelected(newSelected);
      onChange(newSelected);
    },
    [onChange]
  );

  // Add local state for number input
  const [localNumberValue, setLocalNumberValue] = useState(stringValue);

  // Update local number value when prop value changes
  useEffect(() => {
    setLocalNumberValue(stringValue);
  }, [stringValue]);

  // Phone number example: parse string as JSON object
  if (id === "phoneNumber") {
    return (
      <PhoneNumberField
        value={stringValue}
        onChange={(newE164) => onChange(newE164)}
        phoneLabel={computedLabel}
        phoneHelpText={helpText}
        phoneError={error}
        disabled={disabled}
        isRequired={isRequired}
      />
    );
  }

  // Switch for other field types
  switch (id) {
    case "number":
      return (
        <NumberRangeField
          label={computedLabel}
          value={localNumberValue}
          onChange={(value) => {
            setLocalNumberValue(value);
            debouncedOnChange(value);
          }}
          disabled={disabled}
          helpText={helpText}
          placeholder={computedPlaceholder}
          error={error}
          requiredIndicator={isRequired}
        />
      );
    case "textInput":
    case "textArea":
    case "email":
      return (
        <TextField
          label={computedLabel}
          value={localValue}
          onChange={(value) => {
            setLocalValue(value);
            debouncedOnChange(value);
          }}
          type={id === "email" ? "email" : "text"}
          multiline={id === "textArea" ? 4 : undefined}
          disabled={disabled}
          helpText={helpText}
          placeholder={computedPlaceholder}
          autoComplete="off"
          error={error}
          requiredIndicator={isRequired}
        />
      );

    case "checkbox":
      return (
        <BlockStack gap="200">
          <Text variant="headingSm" as="h6">
            {computedLabel}
          </Text>
          <BlockStack gap="0">
            <RadioButton
              label={t("common.yes")}
              checked={value === "true"}
              id={`${id}-yes`}
              name={id}
              onChange={() => onChange("true")}
              disabled={disabled}
            />
            <RadioButton
              label={t("common.no")}
              checked={value === "false"}
              id={`${id}-no`}
              name={id}
              onChange={() => onChange("false")}
              disabled={disabled}
            />
            <RadioButton
              label={t("common.notSet")}
              checked={value === "" || value == null}
              id={`${id}-notset`}
              name={id}
              onChange={() => onChange(null)}
              disabled={disabled}
            />
          </BlockStack>

          {helpText && <Text as="p">{helpText}</Text>}
        </BlockStack>
      );

    case "date":
      return (
        <DateRangeList
          value={stringValue}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case "combobox":
    case "combeBoxMultiple":
      return (
        <BlockStack gap="200">
          <Text variant="headingSm" as="h4">
            {computedLabel}
          </Text>
          <MultiSelect
            options={comboOptions as unknown as MultiComboOption[]}
            selected={multiSelected}
            onChange={handleMultiSelectChange}
            label={computedLabel}
            placeholder={computedPlaceholder}
          />
          {error && (
            <Text tone="critical" as="p">
              {error}
            </Text>
          )}
        </BlockStack>
      );
    case "teamMembers":
      return (
        <BlockStack gap="200">
          <Text variant="headingSm" as="h4">
            {computedLabel}
          </Text>
          <TeammemberSelect
            options={comboOptions as unknown as Member[]}
            selected={(value || []) as string[]}
            onChange={onChange}
            label={computedLabel}
            placeholder={computedPlaceholder}
          />
          {error && (
            <Text tone="critical" as="p">
              {error}
            </Text>
          )}
        </BlockStack>
      );

    case "dropZone":
      return (
        <BlockStack gap="200">
          <Text as="h3" variant="bodyMd">
            {computedLabel}
          </Text>
          {file ? (
            <BlockStack key={file.name}>
              <Thumbnail
                source={URL.createObjectURL(file)}
                alt={file.name}
                size="large"
              />
              <Text as="p">{file.name}</Text>
              <Box>
                <Button
                  variant="tertiary"
                  onClick={() => onRemoveFile && onRemoveFile(file)}
                  tone="critical"
                >
                  {t("common.remove")}
                </Button>
              </Box>
            </BlockStack>
          ) : (
            <DropZone
              onDrop={onDrop}
              disabled={disabled}
              allowMultiple={false}
              type="image"
              accept="image/*"
            >
              <DropZone.FileUpload
                actionTitle={
                  computedPlaceholder ||
                  t("FilterComponentRenderer.uploadFiles")
                }
              />
            </DropZone>
          )}
          {error && (
            <Text tone="critical" as="p">
              {error}
            </Text>
          )}
          {helpText && <TranslateLink text={helpText} />}
        </BlockStack>
      );

    default:
      return (
        <TextField
          autoComplete="off"
          label={
            computedLabel ||
            t("FilterComponentRenderer.unknownTypeLabel")
          }
          value={localValue}
          onChange={(value) => {
            setLocalValue(value);
            debouncedOnChange(value);
          }}
          disabled={disabled}
          helpText={helpText}
          error={error}
        />
      );
  }
};

/**
 * A shallow comparison of relevant props
 * to avoid re-renders if not needed.
 */
function propsAreEqual(
  prevProps: FilterComponentRendererProps,
  nextProps: FilterComponentRendererProps
): boolean {
  // Check a few key props that might change
  return (
    prevProps.value === nextProps.value &&
    prevProps.error === nextProps.error &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.label === nextProps.label &&
    prevProps.helpText === nextProps.helpText &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.id === nextProps.id &&
    // if comboOptions never changes in your scenario, you can compare .length
    prevProps.comboOptions === nextProps.comboOptions &&
    prevProps.file === nextProps.file &&
    prevProps.onDrop === nextProps.onDrop &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.onRemoveFile === nextProps.onRemoveFile
  );
}

/**
 * Export a memoized version
 */
export const FilterComponentRenderer = React.memo(
  FilterComponentRendererInner,
  propsAreEqual
);
